import { useState } from 'react'
import { useCommonT } from '../../i18n'
import { useConnectivity } from '../../lib/offline/connectivity'
import { useIdentity } from '../../lib/offline/identity'
import { usePendingWork, useRecoveryBlocked } from '../../lib/offline/useOffline'
import { CONN, MODE } from '../../lib/offline/offline.codes'
import AuthModal from '../auth/AuthModal'
import ModalShell from '../shared/ModalShell'
import { OVERLAY_ABOVE_AD, PANEL_FITS_OVERLAY } from '../../lib/adInset'
import OfflineReadyPanel from './OfflineReadyPanel'

/**
 * The offline bar (plan W2): under the header whenever the API is not
 * reachable, the account's session is being restored, or the session was
 * rejected ("Sign in again": the data stays, read-only). Shows the changes
 * waiting to sync and opens the "Offline ready" panel. Renders nothing while
 * online with a usable session and nothing pending, and nothing at all
 * without the offline runtime (C1 then never leaves online).
 */
export default function OfflineBar() {
  const t = useCommonT()
  const conn = useConnectivity()
  const id = useIdentity()
  const pending = usePendingWork()
  const [panelOpen, setPanelOpen] = useState(false)
  const [signInOpen, setSignInOpen] = useState(false)

  const blocked = useRecoveryBlocked()
  const rejected = id.mode === MODE.rejected
  const reconnecting = id.mode === MODE.refreshPending && conn === CONN.online
  const offline = conn !== CONN.online
  if (!blocked && !rejected && !reconnecting && !offline && pending.attention === 0) return null

  const message = blocked
    ? t('offline.bar.cleanup')
    : rejected
    ? t('offline.rejected.body')
    : offline
      ? (conn === CONN.degraded ? t('offline.bar.degraded') : t('offline.bar.offline'))
      : reconnecting ? t('offline.bar.reconnecting') : null

  return (
    <>
      <div
        role="status"
        data-testid="offline-bar"
        data-mode={blocked ? 'blocked' : rejected ? 'rejected' : offline ? conn : reconnecting ? 'reconnecting' : 'attention'}
        className={`w-full px-4 py-2 text-xs flex flex-wrap items-center justify-center gap-x-3 gap-y-1 border-b ${rejected ? 'bg-amber-900/30 border-amber-700/50 text-amber-100' : 'bg-gray-900/90 border-gray-800 text-gray-200'}`}
      >
        {message && <span>{message}</span>}
        {pending.unsynced > 0 && <span className="text-amber-300">{t('offline.bar.pending', { count: pending.unsynced })}</span>}
        {pending.attention > 0 && <span className="text-amber-300">{t('offline.bar.attention', { count: pending.attention })}</span>}
        {(rejected || reconnecting) && (
          <button type="button" data-testid="offline-sign-in-again" onClick={() => setSignInOpen(true)} className="px-2.5 py-1 rounded-md bg-purple-600 text-white font-medium hover:bg-purple-700">
            {t('offline.rejected.sign_in')}
          </button>
        )}
        <button type="button" data-testid="offline-ready-open" onClick={() => setPanelOpen(true)} className="underline underline-offset-2 text-gray-300 hover:text-white">
          {t('offline.bar.ready_link')}
        </button>
      </div>
      {panelOpen && (
        <ModalShell
          onClose={() => setPanelOpen(false)}
          label={t('offline.ready.title')}
          className={`fixed inset-0 z-[90] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 outline-none ${OVERLAY_ABOVE_AD}`}
        >
          <div className={`surface rounded-xl p-6 max-w-md w-full space-y-3 ${PANEL_FITS_OVERLAY}`}>
            <div className="flex items-center justify-between">
              <h2 className="text-base font-semibold text-gray-100">{t('offline.ready.title')}</h2>
              <button type="button" onClick={() => setPanelOpen(false)} className="text-sm text-gray-400 hover:text-white">{t('offline.ready.close')}</button>
            </div>
            <OfflineReadyPanel bare />
          </div>
        </ModalShell>
      )}
      <AuthModal open={signInOpen} onClose={() => setSignInOpen(false)} />
    </>
  )
}
